import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { clubsApi, matchesApi, transfersApi, statsApi, championshipsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import BannerDisplay from '../components/BannerDisplay';
import { useLiveStandings } from '../lib/useLiveStandings';
import {
  Shield,
  Coins,
  Calendar,
  Clock,
  TrendingUp,
  Users,
  Trophy,
  ArrowRight,
  Plus,
  Target,
  Medal,
} from 'lucide-react';

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };
const fadeUp = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } } };

const NeonCard = ({ children, className = '', glow, testId, ...props }) => (
  <motion.div
    variants={fadeUp}
    className={`bg-[#0a0d15] border border-[#00F0FF]/10 p-6 relative overflow-hidden transition-all duration-200 hover:border-[#00F0FF]/25 hover:shadow-[0_0_20px_rgba(0,240,255,0.06)] ${className}`}
    data-testid={testId}
    {...props}
  >
    {glow && <div className={`absolute -top-20 -right-20 w-40 h-40 rounded-full blur-[80px] pointer-events-none ${glow}`} />}
    <div className="relative z-10">{children}</div>
  </motion.div>
);

const StatBox = ({ icon: Icon, label, value, color }) => (
  <div className="bg-[#050508] border border-white/5 p-3 sm:p-4 transition-all duration-200 hover:border-white/10">
    <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
      <Icon size={14} className="sm:w-4 sm:h-4 flex-shrink-0" style={{ color }} />
      <span className="text-[9px] sm:text-[10px] uppercase tracking-widest text-slate-500 truncate">{label}</span>
    </div>
    <p className="font-mono text-xl sm:text-2xl font-bold text-white">{value}</p>
  </div>
);

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isItalian = i18n.language === 'it';
  const [club, setClub] = useState(null);
  const [nextMatch, setNextMatch] = useState(null);
  const [nextMatchClubs, setNextMatchClubs] = useState({ home: null, away: null });
  const [pendingOffers, setPendingOffers] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);
  const [standings, setStandings] = useState([]);
  const [topScorers, setTopScorers] = useState([]);
  const [topAssists, setTopAssists] = useState([]);
  const [allChampStats, setAllChampStats] = useState([]);
  const [championships, setChampionships] = useState([]);
  const [selectedChamp, setSelectedChamp] = useState('');
  const [clubRank, setClubRank] = useState(null);
  const [loading, setLoading] = useState(true);

  // Live standings via WebSocket + polling
  const live = useLiveStandings(selectedChamp);

  // Sync live data into state
  useEffect(() => {
    if (live.standings.length > 0) setStandings(live.standings);
    if (live.topScorers.length > 0) setTopScorers(live.topScorers);
    if (live.topAssists.length > 0) setTopAssists(live.topAssists);
    // Update club rank
    if (user?.club_id && live.standings.length > 0) {
      const idx = live.standings.findIndex(s => s.club_id === user.club_id);
      if (idx >= 0) setClubRank({ position: idx + 1 });
    }
  }, [live.standings, live.topScorers, live.topAssists, user?.club_id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (user?.club_id) {
          const clubRes = await clubsApi.get(user.club_id);
          setClub(clubRes.data);

          const matchesRes = await matchesApi.list({ club_id: user.club_id, status: 'scheduled' });
          if (matchesRes.data.length > 0) {
            const match = matchesRes.data[0];
            setNextMatch(match);
            try {
              const [homeClubRes, awayClubRes] = await Promise.all([
                clubsApi.get(match.home_club_id),
                clubsApi.get(match.away_club_id)
              ]);
              setNextMatchClubs({ home: homeClubRes.data, away: awayClubRes.data });
            } catch (err) {
              console.error('Error fetching match clubs:', err);
            }
          }

          const offersRes = await transfersApi.list({ club_id: user.club_id, status: 'pending' });
          setPendingOffers(offersRes.data.slice(0, 3));
        }

        const statsRes = await statsApi.global();
        setGlobalStats(statsRes.data);

        // Fetch championships
        const champsRes = await championshipsApi.list({ status: 'in_progress' });
        const activeChamps = champsRes.data || [];
        setChampionships(activeChamps);
        if (activeChamps.length > 0) {
          const firstId = activeChamps[0].championship_id;
          setSelectedChamp(firstId);

          // Fetch club-goals for ALL championships
          const allStats = await Promise.all(
            activeChamps.map(async (c) => {
              const [sc, as_, cg] = await Promise.all([
                championshipsApi.topScorers(c.championship_id, 10),
                championshipsApi.topAssists(c.championship_id, 10),
                championshipsApi.clubGoals(c.championship_id),
              ]);
              return { championship_id: c.championship_id, name: c.name, scorers: sc.data || [], assists: as_.data || [], clubGoals: cg.data || [] };
            })
          );
          setAllChampStats(allStats);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const handleChampChange = (champId) => {
    setSelectedChamp(champId);
    // useLiveStandings reacts to selectedChamp change automatically
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div className="space-y-6" variants={stagger} initial="hidden" animate="visible">
      <BannerDisplay className="mb-2" />

      {/* Welcome */}
      <motion.div variants={fadeUp} className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#00F0FF]/60 mb-1 font-mono">
            {isItalian ? 'Dashboard' : 'Dashboard'}
          </p>
          <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold uppercase tracking-tight text-white break-words">
            {t('dashboard.welcome')}, <span className="neon-text-cyan">{user?.gamertag || user?.name}</span>
          </h1>
          <p className="text-slate-500 mt-1 text-sm">{isItalian ? 'Gestisci la tua squadra e domina il campionato' : 'Manage your squad and dominate the championship'}</p>
        </div>
        {!club && (
          <Button onClick={() => navigate('/club')} className="btn-primary px-6 neon-glow-hover" data-testid="create-club-btn">
            <Plus size={18} className="mr-2" />
            {t('dashboard.createClub')}
          </Button>
        )}
      </motion.div>

      {/* Main Grid */}
      <motion.div variants={stagger} className="grid grid-cols-1 md:grid-cols-12 gap-5">
        {/* Club Card */}
        <NeonCard className="md:col-span-8" glow="bg-[#00F0FF]/8" testId="club-card">
          {club ? (
            <div className="flex flex-col md:flex-row gap-6">
              <div
                className="w-24 h-24 md:w-28 md:h-28 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0"
                style={{ backgroundColor: club.colors?.primary || '#0a0d15' }}
              >
                {club.logo ? (
                  <img src={club.logo} alt={club.name} className="w-full h-full object-cover" />
                ) : (
                  <Shield size={40} className="text-white/60" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="font-heading text-xl md:text-2xl font-bold uppercase tracking-tight text-white">{club.name}</h2>
                    <p className="text-xs text-slate-500 uppercase tracking-widest mt-1">{t('dashboard.yourClub')}</p>
                  </div>
                  <Link to="/club" className="text-[#00F0FF] hover:text-[#00F0FF]/80 transition-colors p-2 hover:bg-[#00F0FF]/5" data-testid="view-club-link">
                    <ArrowRight size={20} />
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 mt-5">
                  <StatBox icon={Coins} label={t('dashboard.coins')} value={club.coins?.toLocaleString() || '0'} color="#FFE600" />
                  <StatBox icon={Users} label={t('dashboard.players')} value={club.players?.length || 0} color="#00F0FF" />
                  <StatBox icon={TrendingUp} label={t('dashboard.rank')} value={clubRank ? `${clubRank.position}°` : '--'} color="#00FF87" />
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-10">
              <Shield size={56} className="mx-auto text-[#00F0FF]/15 mb-4" />
              <h3 className="font-heading text-xl font-semibold text-white mb-2">{t('dashboard.noClub')}</h3>
              <p className="text-slate-500 mb-6 text-sm">{isItalian ? 'Crea il tuo club per iniziare a competere' : 'Create your club to start competing'}</p>
              <Button onClick={() => navigate('/club')} className="btn-primary" data-testid="create-club-cta">
                <Plus size={18} className="mr-2" />
                {t('dashboard.createClub')}
              </Button>
            </div>
          )}
        </NeonCard>

        {/* Next Match */}
        <NeonCard className="md:col-span-4" glow="bg-[#FF00E5]/6" testId="next-match-card">
          <div className="flex items-center gap-2 mb-4">
            <Calendar size={16} className="text-[#FF00E5]" />
            <h3 className="font-heading text-base font-semibold uppercase tracking-wider text-white">
              {t('dashboard.nextMatch')}
            </h3>
          </div>
          {nextMatch ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-center flex-1">
                  <div className="w-14 h-14 border border-white/10 mx-auto mb-2 flex items-center justify-center overflow-hidden" style={{ backgroundColor: nextMatchClubs.home?.colors?.primary || '#0a0d15' }}>
                    {nextMatchClubs.home?.logo ? (
                      <img src={nextMatchClubs.home.logo} alt={nextMatchClubs.home.name} className="w-full h-full object-cover" />
                    ) : (
                      <Shield size={24} className="text-white/60" />
                    )}
                  </div>
                  <p className="text-xs text-white truncate font-medium">{nextMatchClubs.home?.name || 'Casa'}</p>
                </div>
                <div className="px-3">
                  <p className="font-heading text-lg font-bold text-[#FF00E5]" style={{ textShadow: '0 0 8px rgba(255,0,229,0.4)' }}>VS</p>
                </div>
                <div className="text-center flex-1">
                  <div className="w-14 h-14 border border-white/10 mx-auto mb-2 flex items-center justify-center overflow-hidden" style={{ backgroundColor: nextMatchClubs.away?.colors?.primary || '#0a0d15' }}>
                    {nextMatchClubs.away?.logo ? (
                      <img src={nextMatchClubs.away.logo} alt={nextMatchClubs.away.name} className="w-full h-full object-cover" />
                    ) : (
                      <Shield size={24} className="text-white/60" />
                    )}
                  </div>
                  <p className="text-xs text-white truncate font-medium">{nextMatchClubs.away?.name || 'Ospite'}</p>
                </div>
              </div>
              <div className="text-center space-y-1 pt-2 border-t border-white/5">
                <p className="text-xs text-slate-400 flex items-center justify-center gap-1.5">
                  <Calendar size={12} className="text-[#00F0FF]/60" />
                  {new Date(nextMatch.match_date).toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
                <p className="flex items-center justify-center gap-1.5">
                  <Clock size={12} className="text-[#FFE600]" />
                  <span className="text-[#FFE600] font-mono font-bold text-sm">
                    {new Date(nextMatch.match_date).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </p>
              </div>
              <Link to="/matches">
                <Button variant="outline" className="w-full text-xs uppercase tracking-wider" data-testid="view-matches-btn">
                  {isItalian ? 'Vedi Tutte le Partite' : 'View All Matches'}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar size={28} className="mx-auto text-[#FF00E5]/20 mb-2" />
              <p className="text-slate-600 text-xs">{isItalian ? 'Nessuna partita in programma' : 'No upcoming matches'}</p>
            </div>
          )}
        </NeonCard>

        {/* Global Stats */}
        <NeonCard className="md:col-span-6" glow="bg-[#00FF87]/6" testId="global-stats-card">
          <div className="flex items-center gap-2 mb-4">
            <Trophy size={16} className="text-[#00FF87]" />
            <h3 className="font-heading text-base font-semibold uppercase tracking-wider text-white">
              {isItalian ? 'Statistiche Lega' : 'League Stats'}
            </h3>
          </div>
          {globalStats && (
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <StatBox icon={Shield} label={isItalian ? 'Club Totali' : 'Total Clubs'} value={globalStats.total_clubs} color="#00F0FF" />
              <StatBox icon={Users} label={isItalian ? 'Giocatori' : 'Players'} value={globalStats.total_players} color="#00FF87" />
              <StatBox icon={Calendar} label={isItalian ? 'Partite Giocate' : 'Matches Played'} value={globalStats.completed_matches} color="#FF00E5" />
              <StatBox icon={Trophy} label={isItalian ? 'Campionati Attivi' : 'Active Leagues'} value={globalStats.active_championships} color="#FFE600" />
            </div>
          )}
        </NeonCard>

        {/* Quick Actions */}
        <NeonCard className="md:col-span-6" testId="quick-actions-card">
          <h3 className="font-heading text-base font-semibold uppercase tracking-wider text-white mb-4">
            {isItalian ? 'Azioni Rapide' : 'Quick Actions'}
          </h3>
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            {[
              { to: '/matches', icon: Calendar, label: isItalian ? 'Partite' : 'Matches', color: '#FF00E5' },
              { to: '/championship', icon: Trophy, label: isItalian ? 'Campionato' : 'Championship', color: '#FFE600' },
              { to: '/find-club', icon: Users, label: isItalian ? 'Trova Club' : 'Find Club', color: '#00FF87' },
              { to: '/shop', icon: Coins, label: isItalian ? 'Shop' : 'Shop', color: '#00F0FF' },
            ].map(item => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`quick-action-${item.to.replace('/', '')}`}
                className="flex items-center gap-3 p-3 bg-[#050508] border border-white/5 transition-all duration-200 hover:border-white/15 hover:bg-[#050508]/80 group"
              >
                <div className="w-9 h-9 flex items-center justify-center border border-white/5 transition-colors group-hover:border-white/15" style={{ backgroundColor: `${item.color}10` }}>
                  <item.icon size={16} style={{ color: item.color }} />
                </div>
                <span className="text-sm text-slate-400 uppercase tracking-wider group-hover:text-white transition-colors">{item.label}</span>
              </Link>
            ))}
          </div>
        </NeonCard>

        {/* Classifica */}
        {championships.length > 0 && (
          <NeonCard className="md:col-span-12" glow="bg-[#FFE600]/6" testId="standings-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Trophy size={16} className="text-[#FFE600]" />
                <h3 className="font-heading text-base font-semibold uppercase tracking-wider text-white">
                  {isItalian ? 'Classifica' : 'Standings'}
                </h3>
              </div>
              {championships.length > 1 && (
                <select
                  value={selectedChamp}
                  onChange={(e) => handleChampChange(e.target.value)}
                  className="bg-[#050508] border border-white/10 text-white text-xs px-2 py-1 rounded"
                  data-testid="standings-champ-select"
                >
                  {championships.map(c => (
                    <option key={c.championship_id} value={c.championship_id}>{c.name}</option>
                  ))}
                </select>
              )}
              {championships.length === 1 && (
                <span className="text-xs text-slate-500 uppercase tracking-wider">{championships[0].name}</span>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="standings-table">
                <thead>
                  <tr className="border-b border-white/10 text-[10px] uppercase tracking-widest text-slate-500">
                    <th className="text-left py-2 px-2 w-8">#</th>
                    <th className="text-left py-2 px-2">Club</th>
                    <th className="text-center py-2 px-1">PG</th>
                    <th className="text-center py-2 px-1">V</th>
                    <th className="text-center py-2 px-1">P</th>
                    <th className="text-center py-2 px-1 text-[#FF1744]">S</th>
                    <th className="text-center py-2 px-1">GF</th>
                    <th className="text-center py-2 px-1">GS</th>
                    <th className="text-center py-2 px-1">DR</th>
                    <th className="text-center py-2 px-2 text-[#FFE600] font-bold">PT</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.map((s, i) => {
                    const isMyClub = user?.club_id && s.club_id === user.club_id;
                    return (
                      <tr
                        key={s.club_id}
                        className={`border-b border-white/5 transition-colors ${isMyClub ? 'bg-[#00F0FF]/5' : 'hover:bg-white/[0.02]'}`}
                        data-testid={`standing-row-${i}`}
                      >
                        <td className="py-2 px-2 font-mono text-xs text-slate-400">{i + 1}</td>
                        <td className="py-2 px-2">
                          <span className={`text-sm font-medium truncate ${isMyClub ? 'text-[#00F0FF]' : 'text-white'}`}>
                            {s.club_name}
                          </span>
                        </td>
                        <td className="text-center py-2 px-1 font-mono text-xs text-slate-400">{s.played}</td>
                        <td className="text-center py-2 px-1 font-mono text-xs text-[#00E676]">{s.won}</td>
                        <td className="text-center py-2 px-1 font-mono text-xs text-slate-400">{s.drawn}</td>
                        <td className="text-center py-2 px-1 font-mono text-xs text-[#FF1744]">{s.lost}</td>
                        <td className="text-center py-2 px-1 font-mono text-xs text-slate-400">{s.goals_for}</td>
                        <td className="text-center py-2 px-1 font-mono text-xs text-slate-400">{s.goals_against}</td>
                        <td className="text-center py-2 px-1 font-mono text-xs text-slate-300">{s.goal_difference > 0 ? '+' : ''}{s.goal_difference}</td>
                        <td className="text-center py-2 px-2 font-mono text-sm font-bold text-[#FFE600]">{s.points}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </NeonCard>
        )}

        {/* Classifica Marcatori & Assistman & Gol Squadra per ogni campionato */}
        {allChampStats.filter(cs => cs.scorers.length > 0 || cs.assists.length > 0 || cs.clubGoals.length > 0).map((cs) => (
          <div key={cs.championship_id} className="md:col-span-12 space-y-2" data-testid={`stats-section-${cs.championship_id}`}>
            <h3 className="font-heading text-sm uppercase tracking-widest text-slate-400 px-1">{cs.name}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Marcatori */}
              <NeonCard glow="bg-[#00E676]/6" testId={`scorers-${cs.championship_id}`}>
                <div className="flex items-center gap-2 mb-4">
                  <Target size={16} className="text-[#00E676]" />
                  <h3 className="font-heading text-base font-semibold uppercase tracking-wider text-white">
                    {isItalian ? 'Classifica Marcatori' : 'Top Scorers'}
                  </h3>
                </div>
                {cs.scorers.length > 0 ? (
                  <div className="space-y-1">
                    {cs.scorers.map((p, i) => (
                      <div key={p.player_id} className={`flex items-center gap-3 py-2 px-2 ${i === 0 ? 'bg-[#00E676]/5 border border-[#00E676]/15' : 'border-b border-white/5'}`}>
                        <span className={`font-mono text-xs w-5 text-center ${i === 0 ? 'text-[#00E676] font-bold' : i < 3 ? 'text-[#FFE600]' : 'text-slate-500'}`}>{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${i === 0 ? 'text-[#00E676]' : 'text-white'}`}>{p.player_name}</p>
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider truncate">{p.club_name}</p>
                        </div>
                        <span className="font-mono text-lg font-bold text-[#00E676]">{p.goals}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">{isItalian ? 'Nessun gol segnato' : 'No goals scored'}</p>
                )}
              </NeonCard>

              {/* Assistman */}
              <NeonCard glow="bg-[#00F0FF]/6" testId={`assists-${cs.championship_id}`}>
                <div className="flex items-center gap-2 mb-4">
                  <Medal size={16} className="text-[#00F0FF]" />
                  <h3 className="font-heading text-base font-semibold uppercase tracking-wider text-white">
                    {isItalian ? 'Classifica Assistman' : 'Top Assists'}
                  </h3>
                </div>
                {cs.assists.length > 0 ? (
                  <div className="space-y-1">
                    {cs.assists.map((p, i) => (
                      <div key={p.player_id} className={`flex items-center gap-3 py-2 px-2 ${i === 0 ? 'bg-[#00F0FF]/5 border border-[#00F0FF]/15' : 'border-b border-white/5'}`}>
                        <span className={`font-mono text-xs w-5 text-center ${i === 0 ? 'text-[#00F0FF] font-bold' : i < 3 ? 'text-[#FFE600]' : 'text-slate-500'}`}>{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${i === 0 ? 'text-[#00F0FF]' : 'text-white'}`}>{p.player_name}</p>
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider truncate">{p.club_name}</p>
                        </div>
                        <span className="font-mono text-lg font-bold text-[#00F0FF]">{p.assists}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">{isItalian ? 'Nessun assist registrato' : 'No assists recorded'}</p>
                )}
              </NeonCard>
            </div>
          </div>
        ))}
      </motion.div>
    </motion.div>
  );
};

export default Dashboard;
